/**
 * derive-trend-stats — small helpers that pull editorial-friendly statements
 * out of the raw `DashboardStats.driftTrends` series.
 *
 * Lives in `src/lib` (not next to a particular page) so all three Posture
 * pages and the Command Center triptych can share one source of truth for
 * phrases like "9 days running" or the trend deck sentence.
 */

import type { TrendData } from '@/types'

/**
 * A `TrendData[]` is the canonical drift-trend series the API returns. The
 * helpers here only need a `.date` and the per-severity counts, so we accept
 * the public shape directly — no internal narrowing.
 */
type DriftTrend = Pick<TrendData, 'date' | 'critical' | 'high' | 'medium' | 'low' | 'info'>

/** Sum the severity buckets on a single trend point. */
function totalDriftsOpened(point: DriftTrend): number {
  return (
    (point.critical ?? 0) +
    (point.high ?? 0) +
    (point.medium ?? 0) +
    (point.low ?? 0) +
    (point.info ?? 0)
  )
}

/**
 * Verdict-style summary of a drift-trend series, designed for the Configuration
 * Drift page's "Section · 01 · Drift trend" headline. Returns the editorial
 * triplet the chart wants to lead with:
 *
 *   - `verdict`     — a ≤6-word phrase that names the movement (Voice rule #1).
 *                     Picked from a small set so all surfaces stay consistent.
 *   - `windowTotal` — the count of drifts OPENED across the 30-day window
 *                     (sum of every daily total). The number we need most.
 *   - `recentTotal` — drifts opened in the trailing 7 days.
 *   - `priorTotal`  — drifts opened in the 7 days BEFORE that (for the deck).
 *   - `direction`   — 'rising' | 'easing' | 'flat' | null, derived from the
 *                     trailing-7 vs prior-7 deltas. Null when there isn't enough
 *                     data to compare two full weeks (≥8 points required).
 *
 * Returns `null` when the series is empty, so callers can render a graceful
 * empty state without doing the math themselves.
 *
 * "Opened" framing matches the chart's own caption: each daily bucket on the
 * wire is the count of NEW drifts opened that day across all workloads. The
 * sum is a window total, not a snapshot — for the live snapshot the caller
 * should pair this with `DashboardStats.activeDrifts`.
 */
export interface DriftTrendSummary {
  verdict: string
  windowTotal: number
  recentTotal: number
  priorTotal: number
  direction: 'rising' | 'easing' | 'flat' | null
}

export function summarizeDriftTrend(
  trends: ReadonlyArray<DriftTrend> | null | undefined,
): DriftTrendSummary | null {
  if (!Array.isArray(trends) || trends.length === 0) return null

  const totals = trends.map(totalDriftsOpened)
  const windowTotal = totals.reduce((a, b) => a + b, 0)

  // Need at least two full weeks to compare; otherwise the direction is null
  // and the verdict falls back to a count-only statement.
  let recentTotal = 0
  let priorTotal = 0
  let direction: DriftTrendSummary['direction'] = null
  if (totals.length >= 8) {
    recentTotal = totals.slice(-7).reduce((a, b) => a + b, 0)
    priorTotal = totals.slice(-14, -7).reduce((a, b) => a + b, 0)
    if (recentTotal === 0 && priorTotal === 0) {
      direction = 'flat'
    } else if (priorTotal === 0) {
      direction = 'rising'
    } else {
      const deltaPct = ((recentTotal - priorTotal) / priorTotal) * 100
      if (Math.abs(deltaPct) < 5) direction = 'flat'
      else if (deltaPct > 0) direction = 'rising'
      else direction = 'easing'
    }
  }

  // Verdict picker — six words or fewer, no em-dashes, period-then-newline
  // friendly. The "flat at zero" case earns its own line so the operator
  // doesn't read "Drift is settled" while the queue is still piling up.
  let verdict: string
  if (windowTotal === 0) {
    verdict = 'No drift opened this month.'
  } else if (direction === 'easing') {
    verdict = 'Drift is easing.'
  } else if (direction === 'rising') {
    verdict = 'Drift is climbing.'
  } else if (direction === 'flat') {
    verdict = 'Drift is steady.'
  } else {
    // Direction unknown (series shorter than 8 days). Stay neutral.
    verdict = 'Drift, last 30 days.'
  }

  return { verdict, windowTotal, recentTotal, priorTotal, direction }
}

/**
 * Number of consecutive trailing days where the drift count was non-increasing
 * (i.e. each day held flat or improved versus the day before). Returns the
 * count of days, INCLUDING the most recent day, where the run still holds.
 *
 * The series is assumed to be sorted oldest → newest, matching what
 * `DashboardStats.driftTrends` already produces.
 *
 * Returns `null` when the series is too short to compute a meaningful run
 * (we want at least three days so a single down-tick doesn't read as a
 * "9-day trend"). This mirrors the editorial rule from the redesign brief —
 * don't surface the statement at all if data isn't there to back it.
 */
export function consecutiveImprovementDays(
  trends: ReadonlyArray<DriftTrend> | null | undefined,
): number | null {
  if (!Array.isArray(trends) || trends.length < 3) {
    return null
  }

  const totals = trends.map(totalDriftsOpened)

  // Walk backwards from the latest point. The run includes the latest day
  // itself — a series ending in [5, 4, 4] is a 3-day non-increasing streak —
  // and stops the moment we see an increase.
  let streak = 1
  for (let i = totals.length - 1; i > 0; i--) {
    if (totals[i] <= totals[i - 1]) {
      streak += 1
    } else {
      break
    }
  }

  // A streak of 1 means the latest point is already higher than the prior
  // day — there's no improvement story to tell. Return null so callers can
  // skip the sentence entirely (the editorial rule).
  if (streak < 2) return null

  return streak
}
